import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { firstValueFrom } from 'rxjs'

/**
 * Runtime configuration interface.
 * It contains all the properties that are loaded from the config.json file and
 * that could change depending on the env where the application is deployed.
 */
export interface RuntimeConfig {
   environment: string
   linkingToolUrl: string
   pmpToolUrl: string
   budgetToolUrl: string
   peopleToolUrl: string

   frToolUrl: string
   praktiklerenToolUrl: string
   wbsoToolUrl: string
   wheesbeeToolUrl: string
   timeTrackingToolUrl: string
   euAccademyToolUrl: string
   crmToolUrl: string
   cleanAviationToolUrl: string
   ciriosToolUrl: string
   hubspotToolUrl: string
   showMaintenanceDialog: boolean
   maintenanceDialogMessage: string
   garbiToolUrl: string
   campiToolUrl: string
   feedbackToolUrl: string
}

/**
 * Service that provides the runtime configuration of the application.
 * It loads the configuration from a remote endpoint and provides it to the application.
 */
@Injectable({
   providedIn: 'root',
})
export class ConfigService {
   private readonly RUNTIME_CONFIG_PATH = 'assets/configs/properties.json'

   private config: RuntimeConfig = {
      environment: 'NONE',
      linkingToolUrl: '',
      pmpToolUrl: '',
      budgetToolUrl: '',
      peopleToolUrl: '',
      frToolUrl: '',
      praktiklerenToolUrl: '',
      wbsoToolUrl: '',
      wheesbeeToolUrl: '',
      timeTrackingToolUrl: '',
      euAccademyToolUrl: '',
      crmToolUrl: '',
      cleanAviationToolUrl: '',
      ciriosToolUrl: '',
      hubspotToolUrl: '',
      showMaintenanceDialog: false,
      maintenanceDialogMessage: '',
      garbiToolUrl: '',
      campiToolUrl: '',
      feedbackToolUrl: '',
   }

   constructor(private httpClient: HttpClient) {}

   /**
    * Initializes the dynamic configuration from the config.json file.
    * @returns {Promise<RuntimeConfig>} The dynamic configuration.
    */
   initRuntimeConfig(): Promise<RuntimeConfig> {
      return firstValueFrom(this.httpClient.get<RuntimeConfig>(`${this.RUNTIME_CONFIG_PATH}?ts=${Date.now()}`)).then((dc) => {
         if (!dc) {
            throw new Error('Runtime configuration not found')
         }
         this.config = dc
         console.log('Runtime Configuration loaded for env: ', this.environment)
         return dc
      })
   }

   get environment(): string {
      return this.config.environment
   }

   get linkingToolUrl(): string {
      return this.config.linkingToolUrl
   }

   get pmpToolUrl(): string {
      return this.config.pmpToolUrl
   }

   get budgetToolUrl(): string {
      return this.config.budgetToolUrl
   }

   get peopleToolUrl(): string {
      return this.config.peopleToolUrl
   }

   get frToolUrl(): string {
      return this.config.frToolUrl
   }

   get praktiklerenToolUrl(): string {
      return this.config.praktiklerenToolUrl
   }

   get wbsoToolUrl(): string {
      return this.config.wbsoToolUrl
   }

   get wheesbeeToolUrl(): string {
      return this.config.wheesbeeToolUrl
   }

   get timeTrackingToolUrl(): string {
      return this.config.timeTrackingToolUrl
   }

   get euAccademyToolUrl(): string {
      return this.config.euAccademyToolUrl
   }

   get crmToolUrl(): string {
      return this.config.crmToolUrl
   }

   get showMaintenanceDialog(): boolean {
      return this.config.showMaintenanceDialog
   }

   get maintenanceDialogMessage(): string {
      return this.config.maintenanceDialogMessage
   }

   get cleanAviationToolUrl(): string {
      return this.config.cleanAviationToolUrl
   }

   get ciriosToolUrl(): string {
      return this.config.ciriosToolUrl
   }

   get hubspotToolUrl(): string {
      return this.config.hubspotToolUrl
   }

   get garbiToolUrl(): string {
      return this.config.garbiToolUrl
   }

   get campiToolUrl(): string {
      return this.config.campiToolUrl
   }

   get feedbackToolUrl(): string {
      return this.config.feedbackToolUrl
   }
}
